import { useEffect, useState } from "react";
import {
  ArrowUpRight,
  CheckCircle2,
  ChevronDown,
  Clock,
  Gift,
  Heart,
  Loader2,
  Phone,
  Scale,
  Sparkles,
  Star,
} from "lucide-react";
import CustomerUnpaidPaySection from "@/components/mobile/CustomerUnpaidPaySection";
import CustomerLiveTracking from "@/components/mobile/CustomerLiveTracking";
import CustomerProductionTimeline from "@/components/mobile/CustomerProductionTimeline";
import FullWidthImageLightbox from "@/components/mobile/FullWidthImageLightbox";
import CustomerNutritionLabel from "@/components/mobile/CustomerNutritionLabel";
import { OrderBakingSpecDisplay } from "@/components/OrderBakingSpecDisplay";
import {
  CUSTOMER_PROOF_CALL_HREF,
  CUTLERY_POUCH_IMAGE_SRC,
  FATHERS_DAY_OFFER_URL,
  FATHERS_DAY_PROMO_IMAGE_SRC,
  TABLETOP_LOGO_SRC,
} from "@/lib/customerProofPageStyles";

function proofImageSrc(url) {
  if (!url || typeof url !== "string") return url;
  try {
    return new URL(url, window.location.origin).href;
  } catch {
    return encodeURI(url);
  }
}

function formatWeightLabel(weightKg) {
  const w = Number(weightKg);
  if (!Number.isFinite(w) || w <= 0) return null;
  return `${Math.round(w * 1000)} g`;
}

function customerFirstName(name) {
  const raw = String(name || "").trim();
  if (!raw) return "there";
  const first = raw.split(/\s+/)[0];
  if (!first) return "there";
  return first.charAt(0).toUpperCase() + first.slice(1).toLowerCase();
}

function formatCountdown(seconds) {
  const s = Math.max(0, Number(seconds) || 0);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, "0")}`;
}

function ProofShot({ url, label, onOpen, tall }) {
  const src = proofImageSrc(url);
  if (!src) return null;
  return (
    <button type="button" className="customer-proof-shot" onClick={() => onOpen(src, label)}>
      <img
        src={src}
        alt={label}
        loading="lazy"
        decoding="async"
        style={tall ? { aspectRatio: "4/5" } : undefined}
      />
      <div className="customer-proof-shot-label">{label}</div>
    </button>
  );
}

function AccordionSection({ title, open, onToggle, children, className = "" }) {
  return (
    <div className={`customer-proof-section${className ? ` ${className}` : ""}`}>
      <button type="button" className="customer-proof-section-toggle" onClick={onToggle}>
        <span>{title}</span>
        <ChevronDown
          size={20}
          className={`customer-proof-section-chevron${open ? " customer-proof-section-chevron--open" : ""}`}
          aria-hidden
        />
      </button>
      {open ? <div className="customer-proof-section-body">{children}</div> : null}
    </div>
  );
}

function warmFathersDayOffer() {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  const href = new URL(FATHERS_DAY_OFFER_URL, window.location.origin).href;
  if (!document.querySelector("link[data-fathers-day-offer='prefetch']")) {
    const link = document.createElement("link");
    link.rel = "prefetch";
    link.href = href;
    link.as = "document";
    link.setAttribute("data-fathers-day-offer", "prefetch");
    document.head.appendChild(link);
  }
  fetch(href, { cache: "force-cache" }).catch(() => {});
}

function FathersDayPromoBlock({ compact = false }) {
  return (
    <a
      className={`cp-fathers-day-promo${compact ? " cp-fathers-day-promo--compact" : ""}`}
      href={FATHERS_DAY_OFFER_URL}
      onPointerDown={warmFathersDayOffer}
      onMouseEnter={warmFathersDayOffer}
      onFocus={warmFathersDayOffer}
      aria-label="Open The Table Top Father's Day offer"
    >
      <span className="cp-fathers-day-glow" aria-hidden />
      <span className="cp-fathers-day-media">
        <img
          src={FATHERS_DAY_PROMO_IMAGE_SRC}
          alt="Father's Day cake offer from The Table Top"
          loading="lazy"
          decoding="async"
        />
        <span className="cp-fathers-day-badge">
          <Gift size={14} strokeWidth={2.6} aria-hidden />
          Father&apos;s Day Special
        </span>
      </span>
      <span className="cp-fathers-day-content">
        <span className="cp-fathers-day-icon" aria-hidden>
          <Gift size={20} strokeWidth={2.5} />
        </span>
        <span className="cp-fathers-day-copy">
          <span className="cp-fathers-day-kicker">Limited offer</span>
          <span className="cp-fathers-day-title">Make Dad&apos;s day sweeter</span>
          <span className="cp-fathers-day-sub">Tap to see our Father&apos;s Day cake collection</span>
        </span>
        <span className="cp-fathers-day-action" aria-hidden>
          <ArrowUpRight size={18} strokeWidth={2.6} />
        </span>
      </span>
    </a>
  );
}

function ProofChecklistRows({ items }) {
  if (!items?.length) return null;
  return (
    <div className="cp-checklist-rows">
      {items.map((item) => (
        <div key={item.key} className="cp-check-row">
          <div className="cp-check-icon" aria-hidden>
            <CheckCircle2 size={16} strokeWidth={2.5} />
          </div>
          <div className="cp-check-copy">
            <span className="cp-check-label">{item.label}</span>
            {item.detail ? <span className="cp-check-detail">{item.detail}</span> : null}
          </div>
        </div>
      ))}
    </div>
  );
}

function ProofTimerDisplay({ remaining, totalSeconds = 600 }) {
  const pct = Math.max(0, Math.min(100, (remaining / totalSeconds) * 100));
  const urgent = remaining > 0 && remaining <= 120;

  return (
    <div
      className={`cp-timer-block${urgent ? " cp-timer-block--urgent" : ""}`}
      aria-live="polite"
    >
      <div className="cp-timer-ring-wrap">
        <svg className="cp-timer-ring" viewBox="0 0 120 120" aria-hidden>
          <circle className="cp-timer-ring-track" cx="60" cy="60" r="52" />
          <circle
            className="cp-timer-ring-progress"
            cx="60"
            cy="60"
            r="52"
            style={{ strokeDashoffset: `${326.73 * (1 - pct / 100)}` }}
          />
        </svg>
        <div className="cp-timer-center">
          <Clock size={18} className="cp-timer-clock-icon" aria-hidden />
          <span className="cp-timer-digits">{formatCountdown(remaining)}</span>
        </div>
      </div>
      <p className="cp-timer-caption">Minutes left to review your cake</p>
    </div>
  );
}

function ProofConfirmPanel({ approval, checklist, remaining, onApprove, approving, serviceReview }) {
  if (!approval?.show) return null;

  const approved = approval.is_released || approval.is_approved;
  const reviewPending = approved && !serviceReview?.submitted;

  if (approved && !reviewPending) {
    return (
      <div className="cp-success-banner">
        <CheckCircle2 size={22} aria-hidden />
        <div>
          <p className="cp-success-title">You&apos;re all set!</p>
          <p className="cp-success-text">Your cake is heading out for delivery.</p>
        </div>
      </div>
    );
  }

  if (reviewPending) return null;

  if (approval.waiting_for_decorator) {
    return (
      <div className="cp-confirm-card cp-confirm-card--wait">
        <p className="cp-confirm-wait">
          We&apos;re putting the final touches on your cake. Your 10-minute review window starts as
          soon as decoration is complete.
        </p>
      </div>
    );
  }

  if (!approval.active) return null;

  return (
    <>
      <div className="cp-confirm-card">
        <div className="cp-confirm-head">
          <span className="cp-confirm-eyebrow">Final check</span>
          <h2 className="cp-confirm-title">Everything we matched for you</h2>
          <p className="cp-confirm-sub">
            We verified these on our call — happy with it all? Tap Done and we&apos;ll send your
            cake out.
          </p>
        </div>
        <ProofTimerDisplay remaining={remaining} totalSeconds={approval.timer_total_seconds || 600} />
        {checklist?.length ? <ProofChecklistRows items={checklist} /> : null}
        <p className="cp-confirm-float-hint">
          Tap the pink Done button below when you&apos;re happy with everything.
        </p>
      </div>

      <div className="cp-sticky-bar" role="region" aria-label="Approve your cake">
        <div className="cp-sticky-bar-inner">
          <div className="cp-sticky-timer-row">
            <Clock size={18} aria-hidden />
            <span className="cp-sticky-timer-label">Time left to review</span>
            <span className="cp-sticky-timer-digits">{formatCountdown(remaining)}</span>
          </div>
          <button
            type="button"
            className="cp-sticky-done"
            disabled={!approval.can_approve || approving}
            onClick={onApprove}
          >
            {approving ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
                Sending…
              </>
            ) : (
              "Done — send my cake"
            )}
          </button>
        </div>
      </div>
    </>
  );
}

function ReviewPopup({ serviceReview, onSubmitReview, submitting, onClose }) {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState("");

  if (serviceReview?.submitted) {
    return (
      <div className="cp-review-backdrop" role="dialog" aria-modal aria-label="Review submitted">
        <div className="cp-review-sheet">
          <div className="cp-review-sheet-handle" aria-hidden />
          <span className="cp-review-sheet-thanks-emoji" aria-hidden>
            🎉
          </span>
          <h2 className="cp-review-sheet-thanks-title">Thank you!</h2>
          <p className="cp-review-sheet-thanks-sub">
            Your review means a lot to our small team. Your cake is on its way!
          </p>
          <button type="button" className="cp-review-sheet-close" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    );
  }

  const displayRating = hoverRating || rating;

  return (
    <div className="cp-review-backdrop" role="dialog" aria-modal aria-label="Rate your experience">
      <div className="cp-review-sheet">
        <div className="cp-review-sheet-handle" aria-hidden />
        <div className="cp-review-sheet-emoji" aria-hidden>
          🎂
        </div>
        <h2 className="cp-review-sheet-title">How did we do?</h2>
        <p className="cp-review-sheet-sub">Your cake is heading out — tap a star to rate us!</p>
        <div className="cp-review-sheet-stars" role="radiogroup" aria-label="Star rating">
          {[1, 2, 3, 4, 5].map((star) => {
            const on = star <= displayRating;
            return (
              <button
                key={star}
                type="button"
                className="cp-review-sheet-star-btn"
                aria-label={`${star} star${star === 1 ? "" : "s"}`}
                aria-pressed={rating === star}
                onMouseEnter={() => setHoverRating(star)}
                onMouseLeave={() => setHoverRating(0)}
                onFocus={() => setHoverRating(star)}
                onBlur={() => setHoverRating(0)}
                onClick={() => setRating(star)}
              >
                <Star className={on ? "cp-star-filled" : "cp-star-empty"} aria-hidden />
              </button>
            );
          })}
        </div>
        <textarea
          className="cp-review-sheet-textarea"
          placeholder="Tell us what you loved, or what we can improve (optional)"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          maxLength={2000}
        />
        <FathersDayPromoBlock compact />
        <button
          type="button"
          className="cp-review-sheet-submit"
          disabled={rating < 1 || submitting}
          onClick={() => onSubmitReview?.({ rating, comment })}
        >
          {submitting ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
              Sending…
            </>
          ) : (
            "Submit my rating"
          )}
        </button>
        <button type="button" className="cp-review-sheet-skip" onClick={onClose}>
          Skip for now
        </button>
      </div>
    </div>
  );
}

export default function CustomerProofView({
  info,
  onApprove,
  approving = false,
  onSubmitReview,
  submittingReview = false,
  showReviewPopup = false,
  onCloseReview,
}) {
  const [lightbox, setLightbox] = useState(null);
  const [behindOpen, setBehindOpen] = useState(true);
  const [nutritionOpen, setNutritionOpen] = useState(false);
  const [cutleryOpen, setCutleryOpen] = useState(false);
  const [remaining, setRemaining] = useState(info?.proof_approval?.remaining_seconds ?? 0);

  const approval = info?.proof_approval;
  const checklist = info?.proof_checklist;
  const serviceReview = info?.service_review;
  const approvalActive = Boolean(
    approval?.active && !approval?.is_approved && !approval?.is_released
  );

  useEffect(() => {
    if (!approval?.active || approval.is_approved || approval.is_released) return undefined;
    setRemaining(approval.remaining_seconds ?? 0);
    const tick = setInterval(() => {
      setRemaining((r) => Math.max(0, r - 1));
    }, 1000);
    return () => clearInterval(tick);
  }, [
    approval?.active,
    approval?.is_approved,
    approval?.is_released,
    approval?.remaining_seconds,
    approval?.started_at,
  ]);

  const hasColor = Boolean(
    info?.has_color_proof ?? (info?.reference_image_url && info?.frosted_cake_url)
  );
  const hasDesign = Boolean(info?.has_design_proof ?? info?.cake_image_url);
  const hasWeight = Boolean(info?.has_weight_proof ?? info?.weight_proof_url);
  const hasAnyProof = hasColor || hasDesign || hasWeight;

  const heroUrl = info?.cake_image_url || info?.frosted_cake_url || info?.weight_proof_url;
  const heroSrc = proofImageSrc(heroUrl);
  const heroLabel = info?.cake_image_url
    ? "Your decorated cake"
    : info?.frosted_cake_url
      ? "Your frosted cake"
      : "Your cake on the scale";

  const firstName = customerFirstName(info?.customer_name);
  const weightLabel =
    info?.weight_label && info.weight_label !== "-"
      ? info.weight_label
      : formatWeightLabel(info?.weight_kg);

  const showBehindScenes = hasColor || (hasDesign && info?.reference_image_url) || hasWeight;
  const showCutleryNote = hasDesign || hasWeight;
  const showPaymentDue = Boolean(info?.payment_due?.show);

  const openLightbox = (src, label) => setLightbox({ src, label });

  return (
    <div
      className={`customer-proof-shell${
        approvalActive ? " customer-proof-shell--sticky" : " customer-proof-shell--call-footer"
      }`}
    >
      <header className="customer-proof-top">
        <div className="customer-proof-logo">
          <img src={TABLETOP_LOGO_SRC} alt="The Table Top" />
        </div>
        <div className="customer-proof-top-copy">
          <div className="customer-proof-brand-name">The Table Top</div>
          <div className="customer-proof-brand-tag">Made with care, just for you</div>
        </div>
        {info?.order_number ? <span className="cp-order-chip">#{info.order_number}</span> : null}
      </header>

      {info?.call_before_delivery ? (
        <div className="call-before-delivery-banner" role="alert">
          {info.call_before_delivery_message ||
            "DO NOT RING THE BELL — PLEASE CALL THE CUSTOMER FIRST"}
        </div>
      ) : null}

      {info?.delivery_tracking?.show_map ? (
        <CustomerLiveTracking tracking={info.delivery_tracking} />
      ) : null}

      {hasAnyProof ? (
        <section className="customer-proof-hero-wrap">
          <p className="cp-hero-eyebrow">Your cake is ready</p>
          <h1 className="customer-proof-greeting">
            Hi {firstName}{" "}
            <Sparkles size={22} style={{ display: "inline", verticalAlign: "middle" }} aria-hidden />
          </h1>
          {(info?.flavor || weightLabel) && (
            <p className="cp-hero-sub">{[info?.flavor, weightLabel].filter(Boolean).join(" · ")}</p>
          )}
          {heroSrc ? (
            <button
              type="button"
              className="customer-proof-hero-btn"
              onClick={() => openLightbox(heroSrc, heroLabel)}
            >
              <img src={heroSrc} alt={heroLabel} loading="eager" decoding="async" />
              <div className="customer-proof-hero-caption">
                <span>{heroLabel}</span>
                <span className="customer-proof-hero-tap">Tap to enlarge</span>
              </div>
            </button>
          ) : null}
        </section>
      ) : (
        <div className="customer-proof-empty">
          Your cake photos are being prepared. We&apos;ll message you on WhatsApp when they&apos;re
          ready.
        </div>
      )}

      <CustomerProductionTimeline
        timeline={info?.production_timeline}
        summary={info?.production_timeline_summary}
      />

      <ProofConfirmPanel
        approval={approval}
        checklist={checklist}
        remaining={remaining}
        onApprove={onApprove}
        approving={approving}
        serviceReview={serviceReview}
      />

      {showPaymentDue ? (
        <div className="customer-proof-section customer-proof-section--pay customer-proof-section--pay-open">
          <CustomerUnpaidPaySection paymentDue={info.payment_due} />
        </div>
      ) : null}

      <section className="cp-more-section">
        <p className="cp-more-label">More about your order</p>

        <div className="customer-proof-details">
          {!info?.is_cheesecake ? <OrderBakingSpecDisplay info={info} variant="customer" /> : null}
        </div>

        {showBehindScenes ? (
          <AccordionSection
            title="See how we matched your design"
            open={behindOpen}
            onToggle={() => setBehindOpen((v) => !v)}
          >
            {hasColor ? (
              <>
                <p className="customer-proof-section-hint">
                  Your reference beside our frosted cake — we matched the colour you asked for.
                </p>
                <div className="customer-proof-grid">
                  <ProofShot
                    url={info.reference_image_url}
                    label="Your reference"
                    onOpen={openLightbox}
                  />
                  <ProofShot
                    url={info.frosted_cake_url}
                    label="Our frosted cake"
                    onOpen={openLightbox}
                  />
                </div>
              </>
            ) : null}

            {hasDesign && info?.reference_image_url && info?.cake_image_url ? (
              <div style={{ marginTop: hasColor ? 16 : 0 }}>
                {hasColor ? null : (
                  <p className="customer-proof-section-hint">
                    Your reference beside our finished decoration.
                  </p>
                )}
                <div className="customer-proof-grid">
                  <ProofShot url={info.reference_image_url} label="Your reference" onOpen={openLightbox} />
                  <ProofShot url={info.cake_image_url} label="Our decorated cake" onOpen={openLightbox} />
                </div>
              </div>
            ) : null}

            {hasWeight ? (
              <div style={{ marginTop: hasColor || hasDesign ? 16 : 0 }}>
                <div className="customer-proof-weight-badge">
                  <Scale size={15} aria-hidden />
                  {info.weight_label && info.weight_label !== "-"
                    ? `Ordered: ${info.weight_label}`
                    : "Weight on scale"}
                </div>
                <p className="customer-proof-section-hint">
                  We weighed it — exactly what you ordered.
                </p>
                <div className="customer-proof-stack">
                  <ProofShot url={info.weight_proof_url} label="On scale" onOpen={openLightbox} tall />
                </div>
              </div>
            ) : null}
          </AccordionSection>
        ) : null}

        {info?.nutrition ? (
          <AccordionSection
            title="Nutrition facts"
            open={nutritionOpen}
            onToggle={() => setNutritionOpen((v) => !v)}
          >
            <CustomerNutritionLabel nutrition={info.nutrition} />
          </AccordionSection>
        ) : null}

        {showCutleryNote ? (
          <AccordionSection
            title="What's in your box?"
            open={cutleryOpen}
            onToggle={() => setCutleryOpen((v) => !v)}
          >
            <div className="customer-proof-cutlery-wrap" style={{ margin: 0, padding: "4px 0 0" }}>
              <img
                src={CUTLERY_POUCH_IMAGE_SRC}
                alt="Your cutlery pouch is attached at the back of the box — knife, candle, and topper included"
                className="customer-proof-cutlery-img"
                loading="lazy"
                decoding="async"
              />
            </div>
          </AccordionSection>
        ) : null}

      </section>

      <section className="customer-proof-care customer-proof-care--friendly" aria-label="Before you enjoy">
        <div className="customer-proof-care-head">
          <span className="customer-proof-care-icon" aria-hidden>
            <Heart size={20} strokeWidth={2.25} />
          </span>
          <div>
            <h2 className="customer-proof-care-title">Before you dig in</h2>
            <p className="customer-proof-care-lead">
              A quick note from our kitchen — some cakes have tiny supports, picks, or topper sticks
              holding the design up. Please peek and remove them before serving (especially for kids).
            </p>
          </div>
        </div>
        <ul className="customer-proof-care-list">
          <li>Plastic or wire dowels inside tiered cakes</li>
          <li>Toothpicks, skewers, or floral wire</li>
          <li>Decorative flags, picks, and topper sticks</li>
        </ul>
        <p className="customer-proof-care-note">
          Not sure about something on your cake? Message us on WhatsApp — we&apos;re happy to help.
        </p>
      </section>

      {showReviewPopup ? (
        <ReviewPopup
          serviceReview={serviceReview}
          onSubmitReview={onSubmitReview}
          submitting={submittingReview}
          onClose={onCloseReview}
        />
      ) : null}

      {!approvalActive ? (
        <footer className="customer-proof-call-footer">
          <p className="customer-proof-call-prompt">Need a small change? We&apos;re one call away.</p>
          <a href={CUSTOMER_PROOF_CALL_HREF} className="customer-proof-call-btn">
            <Phone size={22} strokeWidth={2.5} aria-hidden />
            JUST CALL US
          </a>
        </footer>
      ) : null}

      {lightbox ? (
        <FullWidthImageLightbox
          src={lightbox.src}
          label={lightbox.label}
          alt={lightbox.label}
          onClose={() => setLightbox(null)}
        />
      ) : null}
    </div>
  );
}
